import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Pill, Reveal } from "@/components/site/Reveal";
import { PillButton } from "@/components/site/Kit";
import TalkFirst from "@/components/site/TalkFirst";
import { AUDIT, SMALL } from "@/lib/check";
import { PAGES } from "@/lib/sampleAudit";
import { AUDIT_MINUTES, AUDIT_STEPS } from "@/lib/audit";

const NEED = [
  "One person who does the job",
  "One real case to follow",
  "Read-only access, or exports",
  "A decision at the end, either way",
];
const NEVER = [
  "Share a password",
  "Change how you work while we look",
  "Sit through a slide deck",
  "Ask us where things stand",
];

// How a proof becomes a tool. The middle and last lines repeat what the Tool
// and Care already promise on the rest of the site.
const LADDER = [
  [
    "The proof",
    "Day 14 of the Audit",
    "One real case of yours, run through a first version while you watch. It reads and prepares. It runs on our side, and it sends and changes nothing.",
  ],
  [
    "The tool",
    "Fixed price, set in the Audit",
    "Every case, including the awkward ones. Approvals, a log of every action, and written instructions. It runs on your accounts and it is yours to keep.",
  ],
  [
    "Care",
    "Monthly, optional",
    "We keep it working when your software changes, and look at the log and the time saved with you each month.",
  ],
];

const time = (m) => (m === 0 ? "Nothing" : m >= 60 ? "1 hour" : `${m} minutes`);

export default function Audit() {
  return (
    <div data-testid="audit-page">
      <PageHero
        label="The Audit"
        title="From yes to day 14."
        text="One job, two weeks, a fixed fee. It ends with two things: the written Audit, and a first version of the tool doing one real case while you watch."
      />

      <section className="px-5 pt-12 lg:pt-16">
        <dl className="mx-auto grid max-w-[1400px] grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            ["The fee", AUDIT.price],
            ["It takes", AUDIT.time],
            [
              "Your team's time",
              `About ${Math.round(AUDIT_MINUTES / 60)} hours`,
            ],
            ["You end with", "A document and a working proof"],
          ].map(([k, v]) => (
            <div key={k} className="rounded-[20px] bg-[#f7f7f7] p-6">
              <dt className="text-[12px] opacity-45">{k}</dt>
              <dd className="nm-display mt-2 text-[26px] leading-[1.05] tracking-[-0.02em] sm:text-[32px]">
                {v}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="px-5 pt-16 lg:pt-28" data-testid="audit-itinerary">
        <div className="mx-auto max-w-[1400px]">
          <ol className="border-t border-black/10">
            {AUDIT_STEPS.map((s, i) => (
              <Reveal
                as="li"
                key={s.when}
                delay={Math.min(i, 4) * 60}
                className="grid gap-x-8 gap-y-2 border-b border-black/10 py-8 sm:py-10 lg:grid-cols-12"
              >
                <p className="nm-display text-[30px] leading-none tracking-[-0.02em] sm:text-[40px] lg:col-span-3">
                  {s.when}
                </p>
                <div className="lg:col-span-6">
                  <p className="text-[22px] leading-[1.15] tracking-[-0.02em] sm:text-[28px]">
                    {s.what}
                  </p>
                  <p className="mt-3 max-w-[620px] text-[17px] leading-[1.45] opacity-60 sm:text-[19px]">
                    {s.text}
                  </p>
                </div>
                <p className="text-[14px] lg:col-span-2 lg:col-start-11 lg:pt-2 lg:text-right">
                  <span className="block text-[11px] uppercase tracking-[0.08em] opacity-40">
                    Your time
                  </span>
                  {time(s.minutes)}
                </p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-5 pt-16 lg:pt-28">
        <div className="mx-auto grid max-w-[1400px] gap-3 md:grid-cols-2">
          {[
            ["What we need from you", NEED, "bg-[#f7f7f7]", "opacity-70"],
            [
              "What you never have to do",
              NEVER,
              "bg-black text-white",
              "text-white/70",
            ],
          ].map(([head, lines, box, tone]) => (
            <Reveal key={head} className={`rounded-[28px] p-7 sm:p-10 ${box}`}>
              <h2 className="nm-title text-[28px] leading-[1.05] sm:text-[40px]">
                {head}
              </h2>
              <ul className="mt-8 space-y-3">
                {lines.map((line) => (
                  <li
                    key={line}
                    className={`text-[18px] leading-[1.3] sm:text-[20px] ${tone}`}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-5 pt-16 lg:pt-28" data-testid="audit-proof">
        <Reveal className="mx-auto max-w-[1400px]">
          <p className="nm-title max-w-[1100px] text-[34px] leading-[1.04] sm:text-[60px]">
            Most audits end in a recommendation. This one ends with the thing
            running.
          </p>
          <p className="mt-6 max-w-[640px] text-[18px] leading-[1.45] opacity-60 sm:text-[20px]">
            A document can be right and still change nothing. So the last thing
            we do in the two weeks is build a first version and run one real
            case of yours through it, in front of you. You decide about the full
            tool after you have seen it work.
          </p>
        </Reveal>
      </section>

      <section className="px-5 pt-16 lg:pt-28" data-testid="audit-ladder">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <h2 className="nm-title max-w-[900px] text-[30px] leading-[1.08] sm:text-[48px]">
              From the proof to the tool.
            </h2>
          </Reveal>
          <ol className="mt-10 border-t border-black/10 lg:mt-14">
            {LADDER.map(([name, when, text], i) => (
              <Reveal
                as="li"
                key={name}
                delay={i * 70}
                className="grid gap-x-8 gap-y-1 border-b border-black/10 py-7 sm:py-9 lg:grid-cols-12"
              >
                <div className="lg:col-span-4">
                  <p className="text-[22px] leading-[1.15] tracking-[-0.02em] sm:text-[28px]">
                    {name}
                  </p>
                  <p className="mt-1 text-[14px] opacity-45">{when}</p>
                </div>
                <p className="max-w-[720px] text-[18px] leading-[1.4] opacity-60 sm:text-[20px] lg:col-span-8">
                  {text}
                </p>
              </Reveal>
            ))}
          </ol>
          <p className="mt-8 max-w-[720px] text-[16px] leading-[1.45] opacity-60">
            A job under ten hours a month is too small for all of this. For
            those we build one small tool: {SMALL.price},{" "}
            {SMALL.time.toLowerCase()}.{" "}
            <Link to="/scorecard" className="underline underline-offset-2">
              The check tells you which you have.
            </Link>
          </p>
        </div>
      </section>

      <section className="px-5 pt-16 lg:pt-28">
        <Link
          to="/audit/sample"
          data-testid="audit-to-sample"
          className="group mx-auto grid max-w-[1400px] items-end gap-8 rounded-[28px] bg-[#ececec] p-7 transition-colors hover:bg-[#e4e4e4] sm:p-12 lg:grid-cols-12"
        >
          <div className="lg:col-span-7">
            <Pill white>What you hold on day 14</Pill>
            <h2 className="nm-title mt-6 text-[36px] leading-[1] sm:text-[64px]">
              Read a whole one.
            </h2>
            <p className="mt-5 max-w-[560px] text-[18px] leading-[1.4] opacity-60 sm:text-[20px]">
              We ran the Audit on one of our own jobs and published all{" "}
              {PAGES.length} pages, with our own numbers in it.
            </p>
          </div>
          <div className="flex items-center gap-3 text-[15px] font-medium lg:col-span-4 lg:col-start-9 lg:justify-end">
            The sample Audit
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black text-white transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
              <ArrowUpRight size={16} />
            </span>
          </div>
        </Link>
      </section>

      <section className="px-5 pt-16 lg:pt-24">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="nm-title text-[30px] leading-[1.05] sm:text-[44px]">
            Start with the job.
          </p>
          <div className="flex flex-wrap gap-3">
            <PillButton to="/book" size="lg">
              Book the Audit
            </PillButton>
            <TalkFirst location="audit_page" tone="light" />
          </div>
        </div>
      </section>
    </div>
  );
}
